import { useParams } from 'react-router-dom'
import {
  AlertTriangle,
  Banknote,
  Bell,
  Bot,
  Check,
  Clock,
  Coins,
  Download,
  FlaskConical,
  Users as GroupIcon,
  Globe,
  History,
  Landmark,
  Layers,
  Percent,
  Receipt,
  Repeat,
  Scale,
  Search,
  SlidersHorizontal,
  Split,
  Star,
  Store,
  TrendingUp,
  Undo2,
  Wallet,
  X,
} from 'lucide-react'
import type { AdminColumn, EnumOption } from '@/components/DataTable/types'
import { FinancePlanned } from './FinancePlanned'
import { FinanceListTable } from './FinanceListTable'
import { FinanceConfigTable } from './FinanceConfigTable'
import { FinanceQueueTable, type QueueAction } from './FinanceQueueTable'
import {
  bulkDecideRefunds,
  cancelSubscription,
  createManualTxn,
  createRefund,
  createSubscription,
  createWithdrawal,
  decideRefund,
  decideWithdrawal,
  deleteSubscription,
  FIN_STR as S,
  type FinanceRow,
} from './financeApi'

type Col = AdminColumn<FinanceRow>

// ── Shared column atoms ───────────────────────────────────────────────────────
const idCol: Col = { key: 'id', header: 'ID', type: 'id', width: 80 }
const createdCol: Col = { key: 'created_at', header: S.created, type: 'date', width: 150 }
const num = (key: string, header: string, width = 120): Col => ({ key, header, type: 'number', width, align: 'right' })
const txt = (key: string, header: string, width?: number): Col => ({ key, header, type: 'text', width })

const REFUND_STATUS: EnumOption[] = [
  { value: 'pending', label: S.pending, status: 'pending' },
  { value: 'approved', label: S.approved, status: 'approved' },
  { value: 'rejected', label: S.rejected, status: 'rejected' },
]
const WITHDRAWAL_STATUS: EnumOption[] = [
  ...REFUND_STATUS,
  { value: 'paid', label: S.paid, status: 'executed' },
]
const SUB_STATUS: EnumOption[] = [
  { value: 'active', label: S.active, status: 'active' },
  { value: 'cancelled', label: S.cancelled, status: 'cancelled' },
]
const statusCol = (options: EnumOption[]): Col => ({ key: 'status', header: S.status, type: 'enum', width: 130, options })

// ── Reusable workflow actions ─────────────────────────────────────────────────
const pending = (r: FinanceRow) => r.status === 'pending'
const refundActions: QueueAction<FinanceRow>[] = [
  { id: 'approve', label: S.approve, icon: <Check size={14} />, visible: pending, run: (r) => decideRefund(r.id, 'approve') },
  { id: 'reject', label: S.reject, icon: <X size={14} />, danger: true, visible: pending, run: (r) => decideRefund(r.id, 'reject') },
]
const withdrawalActions: QueueAction<FinanceRow>[] = [
  { id: 'approve', label: S.approve, icon: <Check size={14} />, visible: pending, run: (r) => decideWithdrawal(r.id, 'approve') },
  { id: 'reject', label: S.reject, icon: <X size={14} />, danger: true, visible: pending, run: (r) => decideWithdrawal(r.id, 'reject') },
  {
    id: 'paid',
    label: S.markPaid,
    icon: <Banknote size={14} />,
    visible: (r) => r.status === 'pending' || r.status === 'approved',
    run: (r) => decideWithdrawal(r.id, 'paid'),
  },
]

const PERIODS = [
  { value: 'weekly', label: S.weekly },
  { value: 'monthly', label: S.monthly },
  { value: 'yearly', label: S.yearly },
]

/**
 * A10 — Moliya, to'lovlar, Stars. `:id` (A10.03–A10.36) → HAQIQIY funksional ekran.
 * Uch tur: (1) ichki-iqtisod READ hisobotlari (FinanceListTable → real jadvallar),
 * (2) config-CRUD (FinanceConfigTable → finance_* jadvallar), (3) human-workflow
 * (FinanceQueueTable → refund/withdrawal/subscription/manual). Xaritada yo'q id
 * bo'lsa — eski skelet fallback (amalda barchasi qamrab olingan).
 */
export function FinanceReal() {
  const { id = '' } = useParams()

  switch (id) {
    // ── READ: real internal economy ──────────────────────────────────────────
    case 'A10.03':
      return (
        <FinanceListTable
          screenId={id}
          title={S.failedTitle}
          description={S.failedNote}
          reportName="failed"
          searchKeys={['user_ref', 'method', 'reason']}
          icon={<AlertTriangle size={28} strokeWidth={1.5} />}
          columns={[idCol, txt('user_ref', S.user, 140), num('amount', S.amount), txt('method', S.method, 110), { key: 'reason', header: S.reason, type: 'longtext' }, statusCol([{ value: 'open', label: S.pending, status: 'open' }, { value: 'confirmed', label: S.approved, status: 'banned' }, { value: 'cleared', label: S.rejected, status: 'cancelled' }]), createdCol]}
        />
      )
    case 'A10.04':
      return (
        <FinanceListTable
          screenId={id}
          title={S.pendingTitle}
          description={S.pendingNote}
          reportName="pending"
          searchKeys={['seller', 'buyer']}
          icon={<Clock size={28} strokeWidth={1.5} />}
          columns={[idCol, txt('seller', S.seller, 140), txt('buyer', S.buyer, 140), num('price', S.price), num('fee', S.fee), txt('status', S.status, 110), createdCol]}
        />
      )
    case 'A10.05':
      return (
        <FinanceListTable
          screenId={id}
          title={S.byProviderTitle}
          reportName="by-provider"
          searchKeys={['provider']}
          rowId={(r) => String(r.provider)}
          icon={<Split size={28} strokeWidth={1.5} />}
          columns={[txt('provider', S.provider, 200), num('txns', S.txns), num('volume', S.volume, 160), num('net', S.net, 160)]}
        />
      )
    case 'A10.06':
      return (
        <FinanceListTable
          screenId={id}
          title={S.byCurrencyTitle}
          reportName="by-currency"
          searchKeys={['currency']}
          rowId={(r) => String(r.currency)}
          icon={<Coins size={28} strokeWidth={1.5} />}
          columns={[txt('currency', S.currency, 200), num('txns', S.txns), num('volume', S.volume, 160)]}
        />
      )
    case 'A10.07':
      return (
        <FinanceListTable
          screenId={id}
          title={S.byCountryTitle}
          reportName="by-country"
          searchKeys={['country']}
          rowId={(r) => String(r.country)}
          icon={<Globe size={28} strokeWidth={1.5} />}
          columns={[txt('country', S.country, 200), num('txns', S.txns), num('volume', S.volume, 160)]}
        />
      )
    case 'A10.08':
      return (
        <FinanceListTable
          screenId={id}
          title={S.starsTitle}
          reportName="stars-emission"
          searchKeys={['bot']}
          rowId={(r) => String(r.bot_id ?? r.bot)}
          icon={<Star size={28} strokeWidth={1.5} />}
          columns={[txt('bot', S.bot, 180), num('charges', S.charges), num('emitted', S.emitted), num('refunded', S.refunded), num('circulating', S.circulating)]}
        />
      )
    case 'A10.10':
      return (
        <FinanceListTable
          screenId={id}
          title={S.chargebacksTitle}
          reportName="chargebacks"
          searchKeys={['source']}
          rowId={(r) => `${r.source}-${r.ref_id}`}
          icon={<Undo2 size={28} strokeWidth={1.5} />}
          columns={[txt('source', S.source, 120), num('ref_id', S.refId), num('amount', S.amount), { key: 'at', header: S.at, type: 'date', width: 150 }]}
        />
      )
    case 'A10.16':
      return (
        <FinanceListTable
          screenId={id}
          title={S.searchTitle}
          description={S.searchNote}
          reportName="search"
          searchKeys={['username', 'kind', 'note']}
          icon={<Search size={28} strokeWidth={1.5} />}
          columns={[idCol, txt('username', S.user, 150), num('delta', S.amount), txt('kind', S.kind, 130), { key: 'note', header: S.note, type: 'longtext' }, createdCol]}
        />
      )
    case 'A10.17':
      return (
        <FinanceListTable
          screenId={id}
          title={S.reconTitle}
          reportName="reconciliation"
          searchKeys={['username']}
          rowId={(r) => String(r.user_id)}
          icon={<Scale size={28} strokeWidth={1.5} />}
          columns={[{ key: 'user_id', header: 'ID', type: 'id', width: 90 }, txt('username', S.user, 160), num('balance', S.balance), num('ledger_sum', S.ledgerSum, 150), num('diff', S.diff)]}
        />
      )
    case 'A10.18':
      return (
        <FinanceListTable
          screenId={id}
          title={S.providerReconTitle}
          reportName="provider-recon"
          searchKeys={['username']}
          rowId={(r) => String(r.user_id)}
          icon={<Scale size={28} strokeWidth={1.5} />}
          columns={[{ key: 'user_id', header: 'ID', type: 'id', width: 90 }, txt('username', S.user, 160), num('balance', S.balance), num('ledger_sum', S.ledgerSum, 150), num('diff', S.diff)]}
        />
      )
    case 'A10.22':
      return (
        <FinanceListTable
          screenId={id}
          title={S.botRevenueTitle}
          reportName="bot-revenue"
          searchKeys={['bot']}
          rowId={(r) => String(r.bot_id ?? r.bot)}
          icon={<Bot size={28} strokeWidth={1.5} />}
          columns={[txt('bot', S.bot, 200), num('charges', S.charges), num('revenue', S.revenue, 150), num('refunded', S.refunded)]}
        />
      )
    case 'A10.23':
      return (
        <FinanceListTable
          screenId={id}
          title={S.channelMonTitle}
          reportName="channel-monetization"
          searchKeys={['seller']}
          rowId={(r) => String(r.seller_id ?? r.seller)}
          icon={<Store size={28} strokeWidth={1.5} />}
          columns={[txt('seller', S.seller, 200), num('sales', S.sales), num('gross', S.gross, 140), num('fees', S.fee), num('net', S.net, 140)]}
        />
      )
    case 'A10.24':
      return (
        <FinanceListTable
          screenId={id}
          title={S.affiliateTitle}
          reportName="affiliate"
          searchKeys={['referrer']}
          rowId={(r) => String(r.referrer_id ?? r.referrer)}
          icon={<GroupIcon size={28} strokeWidth={1.5} />}
          columns={[txt('referrer', S.referrer, 200), num('referrals', S.referrals), num('points_paid', S.pointsPaid, 160)]}
        />
      )
    case 'A10.27':
      return (
        <FinanceListTable
          screenId={id}
          title={S.cohortTitle}
          reportName="cohort-ltv"
          searchKeys={['cohort']}
          rowId={(r) => String(r.cohort)}
          icon={<TrendingUp size={28} strokeWidth={1.5} />}
          columns={[txt('cohort', S.cohort, 140), num('users', S.users), num('earned', S.earned, 140), num('spent', S.spent, 140), num('ltv', S.ltv)]}
        />
      )
    case 'A10.28':
      return (
        <FinanceListTable
          screenId={id}
          title={S.revenueReportTitle}
          reportName="revenue-report"
          searchKeys={['day']}
          rowId={(r) => String(r.day)}
          icon={<Receipt size={28} strokeWidth={1.5} />}
          columns={[txt('day', S.day, 160), num('events', S.events), num('revenue', S.revenue, 160)]}
        />
      )
    case 'A10.29':
      return (
        <FinanceListTable
          screenId={id}
          title={S.commissionReportTitle}
          reportName="commission-report"
          searchKeys={['scope']}
          rowId={(r) => String(r.scope)}
          icon={<Percent size={28} strokeWidth={1.5} />}
          columns={[txt('scope', S.scope, 200), num('events', S.events), num('total', S.total, 160)]}
        />
      )
    case 'A10.30':
      return (
        <FinanceListTable
          screenId={id}
          title={S.taxReportTitle}
          reportName="tax-report"
          searchKeys={['period']}
          rowId={(r) => String(r.period)}
          icon={<Landmark size={28} strokeWidth={1.5} />}
          columns={[txt('period', S.taxPeriod, 160), num('revenue', S.revenue, 160), num('est_tax', S.estTax, 180)]}
        />
      )
    case 'A10.31':
      return (
        <FinanceListTable
          screenId={id}
          title={S.accountingTitle}
          reportName="accounting-export"
          searchKeys={['username', 'kind', 'note']}
          icon={<Download size={28} strokeWidth={1.5} />}
          columns={[idCol, createdCol, txt('username', S.user, 150), txt('kind', S.kind, 130), num('delta', S.amount), { key: 'note', header: S.note, type: 'longtext' }]}
        />
      )

    // ── CONFIG CRUD: finance_* tables ─────────────────────────────────────────
    case 'A10.32':
      return (
        <FinanceConfigTable
          screenId={id}
          title={S.providerSettingsTitle}
          addLabel={S.addProvider}
          kind="provider-settings"
          searchKeys={['provider', 'mode']}
          icon={<SlidersHorizontal size={28} strokeWidth={1.5} />}
          fields={[
            { name: 'provider', placeholder: S.provider, mono: true },
            { name: 'mode', placeholder: S.mode, options: [{ value: 'live', label: S.live }, { value: 'test', label: S.test }, { value: 'disabled', label: S.disabled }] },
            { name: 'fee_note', placeholder: S.feeNote },
          ]}
          columns={[txt('provider', S.provider, 180), txt('mode', S.mode, 120), txt('fee_note', S.feeNote), { key: 'enabled', header: S.enabled, type: 'bool', width: 90 }, createdCol]}
        />
      )
    case 'A10.33':
      return (
        <FinanceConfigTable
          screenId={id}
          title={S.commissionRatesTitle}
          addLabel={S.addRate}
          kind="commission-rates"
          searchKeys={['scope']}
          icon={<Percent size={28} strokeWidth={1.5} />}
          fields={[
            { name: 'scope', placeholder: S.scope, mono: true },
            { name: 'rate_bps', placeholder: S.rateBps },
            { name: 'min_amount', placeholder: S.minAmount },
            { name: 'note', placeholder: S.note },
          ]}
          columns={[txt('scope', S.scope, 160), txt('rate_bps', S.rateBps, 120), txt('min_amount', S.minAmount, 120), txt('note', S.note), { key: 'enabled', header: S.enabled, type: 'bool', width: 90 }]}
        />
      )
    case 'A10.34':
      return (
        <FinanceConfigTable
          screenId={id}
          title={S.limitsTitle}
          addLabel={S.addLimit}
          kind="payment-limits"
          searchKeys={['scope', 'currency']}
          icon={<Layers size={28} strokeWidth={1.5} />}
          fields={[
            { name: 'scope', placeholder: S.scope, mono: true },
            { name: 'currency', placeholder: S.currency },
            { name: 'min_amount', placeholder: S.minAmount },
            { name: 'max_amount', placeholder: S.maxAmount },
            { name: 'note', placeholder: S.note },
          ]}
          columns={[txt('scope', S.scope, 150), txt('currency', S.currency, 100), txt('min_amount', S.minAmount, 120), txt('max_amount', S.maxAmount, 120), txt('note', S.note), { key: 'enabled', header: S.enabled, type: 'bool', width: 90 }]}
        />
      )
    case 'A10.35':
      return (
        <FinanceConfigTable
          screenId={id}
          title={S.testModeTitle}
          addLabel={S.addArea}
          kind="test-mode"
          searchKeys={['area']}
          icon={<FlaskConical size={28} strokeWidth={1.5} />}
          fields={[
            { name: 'area', placeholder: S.area, mono: true },
            { name: 'note', placeholder: S.note },
          ]}
          columns={[txt('area', S.area, 200), txt('note', S.note), { key: 'enabled', header: S.test, type: 'bool', width: 90 }, createdCol]}
        />
      )
    case 'A10.36':
      return (
        <FinanceConfigTable
          screenId={id}
          title={S.alertsTitle}
          addLabel={S.addAlert}
          kind="alerts"
          searchKeys={['name', 'metric']}
          icon={<Bell size={28} strokeWidth={1.5} />}
          fields={[
            { name: 'name', placeholder: S.alertName, mono: true },
            { name: 'metric', placeholder: S.metric, options: [{ value: 'large_txn', label: 'large_txn' }, { value: 'failed_rate', label: 'failed_rate' }, { value: 'velocity', label: 'velocity' }, { value: 'balance_drop', label: 'balance_drop' }] },
            { name: 'threshold', placeholder: S.threshold },
            { name: 'window_min', placeholder: S.windowMin },
            { name: 'severity', placeholder: S.severity, options: [{ value: 'info', label: 'info' }, { value: 'warning', label: 'warning' }, { value: 'critical', label: 'critical' }] },
            { name: 'note', placeholder: S.note },
          ]}
          columns={[txt('name', S.alertName, 160), txt('metric', S.metric, 130), txt('threshold', S.threshold, 110), txt('window_min', S.windowMin, 110), txt('severity', S.severity, 110), { key: 'enabled', header: S.enabled, type: 'bool', width: 90 }]}
        />
      )

    // ── WORKFLOW: refund / dispute ────────────────────────────────────────────
    case 'A10.11':
      return (
        <FinanceQueueTable<FinanceRow>
          screenId={id}
          title={S.disputesTitle}
          icon={<AlertTriangle size={28} strokeWidth={1.5} />}
          data={{ source: 'refunds', kind: 'dispute', status: '' }}
          searchKeys={['user_ref', 'txn_ref', 'reason']}
          addLabel={S.add}
          fields={[
            { name: 'user_ref', placeholder: S.username },
            { name: 'txn_ref', placeholder: S.txnRef, mono: true },
            { name: 'amount', placeholder: S.amount, type: 'number' },
            { name: 'reason', placeholder: S.reason },
          ]}
          onCreate={(f) =>
            createRefund({ kind: 'dispute', txn_ref: f.txn_ref ?? '', user_ref: f.user_ref ?? '', amount: Number(f.amount) || 0, currency: 'Take', reason: f.reason ?? '' })
          }
          actions={refundActions}
          columns={[idCol, txt('user_ref', S.user, 150), txt('txn_ref', S.txnRef, 130), num('amount', S.amount), { key: 'reason', header: S.reason, type: 'longtext' }, statusCol(REFUND_STATUS), createdCol]}
        />
      )
    case 'A10.12':
      return (
        <FinanceQueueTable<FinanceRow>
          screenId={id}
          title={S.refundQueueTitle}
          icon={<Undo2 size={28} strokeWidth={1.5} />}
          data={{ source: 'refunds', kind: 'refund', status: '' }}
          searchKeys={['user_ref', 'txn_ref', 'reason']}
          addLabel={S.add}
          fields={[
            { name: 'user_ref', placeholder: S.username },
            { name: 'txn_ref', placeholder: S.txnRef, mono: true },
            { name: 'amount', placeholder: S.amount, type: 'number' },
            { name: 'reason', placeholder: S.reason },
          ]}
          onCreate={(f) =>
            createRefund({ kind: 'refund', txn_ref: f.txn_ref ?? '', user_ref: f.user_ref ?? '', amount: Number(f.amount) || 0, currency: 'Take', reason: f.reason ?? '' })
          }
          actions={refundActions}
          columns={[idCol, txt('user_ref', S.user, 150), txt('txn_ref', S.txnRef, 130), num('amount', S.amount), { key: 'reason', header: S.reason, type: 'longtext' }, statusCol(REFUND_STATUS), createdCol]}
        />
      )
    case 'A10.13':
      return (
        <FinanceQueueTable<FinanceRow>
          screenId={id}
          title={S.refundApproveTitle}
          icon={<Check size={28} strokeWidth={1.5} />}
          data={{ source: 'refunds', kind: 'refund', status: 'pending' }}
          searchKeys={['user_ref', 'txn_ref', 'reason']}
          actions={refundActions}
          columns={[idCol, txt('user_ref', S.user, 150), txt('txn_ref', S.txnRef, 130), num('amount', S.amount), { key: 'reason', header: S.reason, type: 'longtext' }, statusCol(REFUND_STATUS), createdCol]}
        />
      )
    case 'A10.14':
      return (
        <FinanceQueueTable<FinanceRow>
          screenId={id}
          title={S.bulkRefundTitle}
          icon={<Layers size={28} strokeWidth={1.5} />}
          data={{ source: 'refunds', kind: 'refund', status: 'pending' }}
          searchKeys={['user_ref', 'txn_ref', 'reason']}
          selectable
          bulkActions={[
            { id: 'approve', label: S.bulkApprove, icon: <Check size={14} />, run: (rows) => bulkDecideRefunds(rows.map((r) => r.id), 'approve') },
            { id: 'reject', label: S.bulkReject, icon: <X size={14} />, danger: true, run: (rows) => bulkDecideRefunds(rows.map((r) => r.id), 'reject') },
          ]}
          columns={[idCol, txt('user_ref', S.user, 150), txt('txn_ref', S.txnRef, 130), num('amount', S.amount), statusCol(REFUND_STATUS), createdCol]}
        />
      )

    // ── WORKFLOW: withdrawals ─────────────────────────────────────────────────
    case 'A10.19':
      return (
        <FinanceQueueTable<FinanceRow>
          screenId={id}
          title={S.withdrawalsTitle}
          icon={<Banknote size={28} strokeWidth={1.5} />}
          data={{ source: 'withdrawals', status: '' }}
          searchKeys={['user_ref', 'method', 'destination']}
          addLabel={S.add}
          fields={[
            { name: 'user_ref', placeholder: S.username },
            { name: 'amount', placeholder: S.amount, type: 'number' },
            { name: 'method', placeholder: S.method },
            { name: 'destination', placeholder: S.destination },
          ]}
          onCreate={(f) =>
            createWithdrawal({ user_ref: f.user_ref ?? '', amount: Number(f.amount) || 0, currency: 'Take', method: f.method ?? '', destination: f.destination ?? '' })
          }
          actions={withdrawalActions}
          columns={[idCol, txt('user_ref', S.user, 150), num('amount', S.amount), txt('method', S.method, 110), txt('destination', S.destination, 160), statusCol(WITHDRAWAL_STATUS), createdCol]}
        />
      )
    case 'A10.20':
      return (
        <FinanceQueueTable<FinanceRow>
          screenId={id}
          title={S.withdrawalApproveTitle}
          icon={<Check size={28} strokeWidth={1.5} />}
          data={{ source: 'withdrawals', status: 'pending' }}
          searchKeys={['user_ref', 'method', 'destination']}
          actions={withdrawalActions}
          columns={[idCol, txt('user_ref', S.user, 150), num('amount', S.amount), txt('method', S.method, 110), txt('destination', S.destination, 160), statusCol(WITHDRAWAL_STATUS), createdCol]}
        />
      )
    case 'A10.21':
      return (
        <FinanceQueueTable<FinanceRow>
          screenId={id}
          title={S.withdrawalHistoryTitle}
          icon={<History size={28} strokeWidth={1.5} />}
          data={{ source: 'withdrawals', status: '' }}
          searchKeys={['user_ref', 'method', 'destination']}
          columns={[idCol, txt('user_ref', S.user, 150), num('amount', S.amount), txt('method', S.method, 110), txt('destination', S.destination, 160), statusCol(WITHDRAWAL_STATUS), { key: 'decided_at', header: S.at, type: 'date', width: 150 }, createdCol]}
        />
      )

    // ── WORKFLOW: subscriptions ───────────────────────────────────────────────
    case 'A10.25':
      return (
        <FinanceQueueTable<FinanceRow>
          screenId={id}
          title={S.subsTitle}
          icon={<Repeat size={28} strokeWidth={1.5} />}
          data={{ source: 'subscriptions', status: 'active' }}
          searchKeys={['user_ref', 'plan']}
          addLabel={S.add}
          fields={[
            { name: 'user_ref', placeholder: S.username },
            { name: 'plan', placeholder: S.plan },
            { name: 'amount', placeholder: S.amount, type: 'number' },
            { name: 'period', placeholder: S.period, options: PERIODS },
          ]}
          onCreate={(f) =>
            createSubscription({ user_ref: f.user_ref ?? '', plan: f.plan ?? '', amount: Number(f.amount) || 0, currency: 'Take', period: f.period || 'monthly' })
          }
          actions={[
            { id: 'cancel', label: S.cancel, icon: <X size={14} />, danger: true, visible: (r) => r.status === 'active', run: (r) => cancelSubscription(r.id) },
            { id: 'delete', label: S.delete, icon: <X size={14} />, danger: true, run: (r) => deleteSubscription(r.id) },
          ]}
          columns={[idCol, txt('user_ref', S.user, 150), txt('plan', S.plan, 140), num('amount', S.amount), txt('period', S.period, 110), statusCol(SUB_STATUS), createdCol]}
        />
      )
    case 'A10.26':
      return (
        <FinanceQueueTable<FinanceRow>
          screenId={id}
          title={S.subsCancelledTitle}
          icon={<X size={28} strokeWidth={1.5} />}
          data={{ source: 'subscriptions', status: 'cancelled' }}
          searchKeys={['user_ref', 'plan']}
          actions={[{ id: 'delete', label: S.delete, icon: <X size={14} />, danger: true, run: (r) => deleteSubscription(r.id) }]}
          columns={[idCol, txt('user_ref', S.user, 150), txt('plan', S.plan, 140), num('amount', S.amount), txt('period', S.period, 110), statusCol(SUB_STATUS), { key: 'cancelled_at', header: S.at, type: 'date', width: 150 }]}
        />
      )

    // ── WORKFLOW: manual compensation (moves real Take wallet) ─────────────────
    case 'A10.15':
      return (
        <FinanceQueueTable<FinanceRow>
          screenId={id}
          title={S.manualTitle}
          description={S.manualNote}
          icon={<Wallet size={28} strokeWidth={1.5} />}
          data={{ source: 'manual' }}
          searchKeys={['username', 'reason']}
          addLabel={S.compensate}
          fields={[
            { name: 'username', placeholder: S.username, mono: true },
            { name: 'delta', placeholder: S.delta, type: 'number' },
            { name: 'reason', placeholder: S.reason },
          ]}
          onCreate={async (f) => {
            const res = await createManualTxn({ username: f.username ?? '', delta: Number(f.delta) || 0, reason: f.reason ?? '' })
            return `${S.compensated} · ${S.balance}: ${res.balance.toLocaleString()}`
          }}
          columns={[idCol, txt('username', S.user, 160), num('delta', S.amount), { key: 'reason', header: S.reason, type: 'longtext' }, createdCol]}
        />
      )

    default:
      return <FinancePlanned />
  }
}

// Re-export for a stable module surface (documentation).
export const FINANCE_REAL_IDS = [
  'A10.03', 'A10.04', 'A10.05', 'A10.06', 'A10.07', 'A10.08', 'A10.10', 'A10.11',
  'A10.12', 'A10.13', 'A10.14', 'A10.15', 'A10.16', 'A10.17', 'A10.18', 'A10.19',
  'A10.20', 'A10.21', 'A10.22', 'A10.23', 'A10.24', 'A10.25', 'A10.26', 'A10.27',
  'A10.28', 'A10.29', 'A10.30', 'A10.31', 'A10.32', 'A10.33', 'A10.34', 'A10.35',
  'A10.36',
] as const
